import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {UserDefinedObjectTemplate} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplate";
import {
    type UserDefinedObjectTemplateAdditionalColumn,
    UserDefinedObjectTemplateAdditionalColumnSourceType as Source,
    UserDefinedObjectTemplateAdditionalColumnType as Type,
} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {OttopilotEventContainerTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTemplate";

function boundTemplate(workspaceId: Uuid, id: Uuid, templates: ReadonlyArray<UserDefinedObjectTemplate>): UserDefinedObjectTemplate {
    const matches = templates.filter((template) => template.id === id && template.workspaceId === workspaceId);
    const match = matches[0];
    if (matches.length !== 1 || match === undefined) {
        throw new Error(`Expected one workspace UDO template for capability binding ${id}`);
    }
    return match;
}

function requireColumn(template: UserDefinedObjectTemplate, id: Uuid, type: Type): UserDefinedObjectTemplateAdditionalColumn {
    const column = template.additionalColumns.find((candidate) => candidate.id === id);
    if (column === undefined || column.type !== type || column.source.type !== Source.physical) {
        throw new Error(`Invalid capability-owned UDO column ${id}`);
    }
    return column;
}

/** Business fields include resolver columns; notes stay a top-level object API field. */
export function getEventContainerAdditionalColumns({
    workspaceId,
    template,
    udoTemplates,
}: {
    workspaceId: Uuid;
    template: Pick<OttopilotEventContainerTemplate, "udoTemplateId"> & {configuration: Pick<OttopilotEventContainerTemplate["configuration"], "notesColumnId" | "statusColumnId">};
    udoTemplates: ReadonlyArray<UserDefinedObjectTemplate>;
}): Array<UserDefinedObjectTemplateAdditionalColumn> {
    const udo = boundTemplate(workspaceId, template.udoTemplateId, udoTemplates);
    requireColumn(udo, template.configuration.notesColumnId, Type.json);
    requireColumn(udo, template.configuration.statusColumnId, Type.uuid);
    return udo.additionalColumns.filter((column) => column.id !== template.configuration.notesColumnId && column.id !== template.configuration.statusColumnId);
}

/** The Event parent and notes columns are exposed through their existing top-level API fields. */
export function getEventAdditionalColumns({
    workspaceId,
    template,
    udoTemplates,
}: {
    workspaceId: Uuid;
    template: Pick<OttopilotEventTemplate, "udoTemplateId"> & {
        configuration: Pick<OttopilotEventTemplate["configuration"], "notesColumnId" | "statusColumnId" | "eventContainerColumnId">;
    };
    udoTemplates: ReadonlyArray<UserDefinedObjectTemplate>;
}): Array<UserDefinedObjectTemplateAdditionalColumn> {
    const udo = boundTemplate(workspaceId, template.udoTemplateId, udoTemplates);
    requireColumn(udo, template.configuration.notesColumnId, Type.json);
    requireColumn(udo, template.configuration.statusColumnId, Type.uuid);
    const reference = requireColumn(udo, template.configuration.eventContainerColumnId, Type.userDefinedObject);
    if (reference.source.type !== Source.physical || !reference.source.required) {
        throw new Error("The Event parent UDO reference must be required");
    }
    return udo.additionalColumns.filter((column) => column.id !== template.configuration.notesColumnId && column.id !== template.configuration.statusColumnId && column.id !== reference.id);
}

export type CapabilityUdoColumnResolver = (
    template: OttopilotEventTemplate | OttopilotEventContainerTemplate | undefined,
) => Array<UserDefinedObjectTemplateAdditionalColumn>;

/** Resolve physical bindings with the current workspace metadata; domain templates contain business configuration only. */
export function createCapabilityUdoColumnResolver(workspaceId: Uuid, udoTemplates: ReadonlyArray<UserDefinedObjectTemplate>): CapabilityUdoColumnResolver {
    const columnsByTemplate = new WeakMap<OttopilotEventTemplate | OttopilotEventContainerTemplate, Array<UserDefinedObjectTemplateAdditionalColumn>>();
    return (template) => {
        if (template === undefined) {
            return [];
        }
        const cached = columnsByTemplate.get(template);
        if (cached !== undefined) {
            return cached;
        }
        const columns =
            "eventContainerColumnId" in template.configuration
                ? getEventAdditionalColumns({
                      workspaceId,
                      template: {udoTemplateId: template.udoTemplateId, configuration: template.configuration},
                      udoTemplates,
                  })
                : getEventContainerAdditionalColumns({workspaceId, template, udoTemplates});
        columnsByTemplate.set(template, columns);
        return columns;
    };
}

/** Domain mutations submit business physical fields only; resolver values and hidden system columns are read-only. */
export function pickPhysicalUdoAdditionalColumnValues(
    columns: ReadonlyArray<UserDefinedObjectTemplateAdditionalColumn>,
    values: Record<Uuid, unknown>,
): Record<Uuid, unknown> {
    const physicalIds = new Set(columns.filter((column) => column.source.type === Source.physical).map((column) => column.id));
    return Object.fromEntries(Object.entries(values).filter(([id]) => physicalIds.has(id as Uuid)));
}
